
import React, { useState, useEffect, useCallback } from 'react';
import { Session } from '@supabase/supabase-js';
import { supabase } from "../integrations/supabase/client";
import { User } from '../types/auth';
import { getUserProfile } from '../services/auth/userProfileService';

export const useAuthState = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSupabaseReady, setIsSupabaseReady] = useState(true);

  const fetchUserProfile = useCallback(async (userId: string, userEmail: string) => {
    try {
      const userProfile = await getUserProfile(userId);
      
      if (userProfile) {
        setUser({
          ...userProfile,
          email: userEmail,
        });
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Function to manually refresh the user data
  const refreshUser = useCallback(async () => {
    if (session?.user) {
      setIsLoading(true);
      await fetchUserProfile(session.user.id, session.user.email || '');
    }
  }, [session, fetchUserProfile]);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        setSession(newSession);
        
        if (event === 'SIGNED_IN' && newSession) {
          // Defer Supabase calls with setTimeout
          setTimeout(async () => {
            await fetchUserProfile(newSession.user.id, newSession.user.email || '');
          }, 0);
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          setIsLoading(false);
        } else if (event === 'USER_UPDATED' && newSession) {
          // If the user is updated, refresh the user data
          setTimeout(async () => {
            await fetchUserProfile(newSession.user.id, newSession.user.email || '');
          }, 0);
        }
      }
    );

    // THEN check for existing session
    const checkSession = async () => {
      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        
        setSession(currentSession);
        
        if (currentSession) {
          await fetchUserProfile(currentSession.user.id, currentSession.user.email || '');
        } else {
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Error checking session:", error);
        setIsLoading(false);
      }
    };
    
    checkSession();
    
    return () => {
      subscription.unsubscribe();
    };
  }, [fetchUserProfile]);

  return {
    user,
    session,
    isLoading,
    isSupabaseReady,
    refreshUser
  };
};
